<template>
  <div class='phone-area'>
    <div class='inputs'>
      <div class='b-input-v4 country_code'>
        <select
          :id='`country_code_${hash}`'
          ref='countryCodeRef'
          :disabled='isDisabled'
        >
          <option
            v-for='countryCode in countryCodes'
            :key='countryCode.label'
            :value='countryCode.imask'
            v-html='countryCode.label'
          />
        </select>
        <label :for='`country_code_${hash}`'>
          {{ tp('country') }}
        </label>
      </div>
      <input
        type='hidden'
        :name='`${prefix}[code]`'
        :value='codeConfirmation?.confirmationCode'
      >
      <div class='b-input-v4 phone'>
        <input
          :id='`phone_${hash}`'
          ref='phoneRef'
          type='tel'
          autocomplete='off'
          :name='`${prefix}[phone]`'
          :placeholder="tp('phone')"
          :disabled='isDisabled'
          @keyup='onPhoneChange'
          @change='onPhoneChange'
          @paste='onPhoneChange'
        >
        <label :for='`phone_${hash}`'>
          {{ tp('phone') }}
        </label>
      </div>
    </div>
    <CodeConfirmation
      v-if='isConfirmation'
      ref='codeConfirmation'
      :phone-number='isPhoneNumberValid ? phoneNumber : null'
      :confirmation-url='confirmationUrl'
      :confirmation-type='confirmationType'
      :resend-code-interval='resendCodeInterval'
      :is-invalid-code='isInvalidCode'
      @confirmation_code:sent='emit("confirmation_code:sent")'
    />
  </div>
</template>

<script>
export default { name: 'PhoneArea' };
</script>

<script setup>
import IMask from 'imask';
import delay from 'delay';
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';

import CustomSelect from '@/views/custom_select';
import CodeConfirmation from './code_confirmation';

import graphqlFetch from '@/helpers/graphql_fetch';
import { prepared } from '@/helpers/i18n';

const props = defineProps({
  prefix: { type: String, required: false, default: '__' },
  graphqlUrl: { type: String, required: true },
  confirmationUrl: { type: String, required: false, default: undefined },
  confirmationType: { type: String, required: false, default: undefined },
  resendCodeInterval: { type: Number, required: false, default: undefined },
  isInvalidCode: { type: Boolean, required: false, default: false },
  value: { type: String, required: false, default: undefined },
  isDisabled: { type: Boolean, required: false, default: false }
});

const tp = prepared('frontend.phone_area.index');
const emit = defineEmits(['component:update', 'confirmation_code:sent']);

const hash = String(Math.floor(Math.random() * 1_000_000_000));
const isConfirmation = !!props.confirmationUrl;

const countryCodeRef = ref(null);
const phoneRef = ref(null);

const customSelect = ref(null);
const iMask = ref(null);
const phoneNumber = ref(props.value || '');

const codeConfirmation = ref(null);
const countryCodesPromise = fetchCountryCodes();
const countryCodes = ref(null);

const isValid = computed(() => (
  isConfirmation ?
    !!codeConfirmation.value.isValid :
    isPhoneNumberValid.value
));
const isPhoneNumberValid = computed(() => (
  !!phoneNumber.value.replace(/\+\d+ ?(\(\d*\)?)?/, '').trim()
));

function syncImask() {
  if (iMask.value) {
    iMask.value.destroy();
  }

  const countryCodeValue = $(countryCodeRef.value).val();
  if (phoneRef.value && countryCodeValue) {
    iMask.value = new IMask(phoneRef.value, {
      mask: countryCodeValue
    });
  }
};

async function onPhoneChange() {
  await delay();
  const currentPhone = phoneRef.value.value;

  phoneNumber.value = currentPhone;
  if (isConfirmation) {
    codeConfirmation.value.reset();
  }

  emit('component:update');
};

function clear() {
  if (phoneRef.value) {
    phoneRef.value.value = '';
  }

  phoneNumber.value = '';
  if (isConfirmation) {
    codeConfirmation.value.reset();
  }

  emit('component:update');

  if (iMask.value) {
    iMask.value.updateValue();
  }
};

async function fetchCountryCodes() {
  if (window.COUNTRY_CODES) {
    return window.COUNTRY_CODES;
  }

  const { items } = await graphqlFetch({
    query: `
        query {
          items:countryCodes {
            imask
            label
          }
        }
      `,
    graphqlUrl: props.graphqlUrl
  });

  return window.COUNTRY_CODES = items;
}

onMounted(async () => {
  phoneRef.value.value = phoneNumber.value;

  if (countryCodesPromise instanceof Promise) {
    countryCodes.value = await countryCodesPromise;
  }

  const countryCodeMatch = phoneNumber.value.match(/^\+\d+/);
  if (countryCodeMatch) {
    const matchedCountryCode = countryCodes.value.find(({ label }) => (
      label.endsWith(countryCodeMatch[0])
    ));
    if (matchedCountryCode) {
      await nextTick();
      // this does not work without `await nextTick()` above
      countryCodeRef.value.value = matchedCountryCode.imask;
    }
  }

  await nextTick();
  customSelect.value = new CustomSelect(countryCodeRef.value.parentElement);

  syncImask();

  $(countryCodeRef.value).on('change', () => {
    syncImask();
    phoneRef.value?.focus();

    emit('component:update');
  });
});

onBeforeUnmount(() => {
  iMask.value?.destroy();
  customSelect.value?.destroy();
});

defineExpose({
  isValid,
  isSent: computed(() => codeConfirmation.value.isSent),
  phoneNumber,
  confirmationCode: computed(() => codeConfirmation.value.confirmationCode),
  clear,
  focus() {
    phoneRef.value.focus();
  }
});
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'

// fixes long country coude which can not fit into its container
.country_code ::v-deep(.placeholder-inner)
  text-overflow: clip

.phone-area
  display: flex
  flex-direction: column
  width: 100%

  +lte_ipad
    gap: rem4(24px)

  +gte_laptop
    gap: 24px

.inputs
  display: flex
  width: 100%

  +lte_ipad
    gap: rem4(16px)

  +gte_laptop
    gap: 16px

  .phone
    flex-grow: 1

  .country_code
    +lte_ipad
      width: rem4(126px)

    +gte_laptop
      width: 126px
</style>
